import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getWishlist, removeFromWishlist, getImageUrl, getCart } from '../api';
import API from '../api';
import { KM_VARS, KM_BASE } from './km-styles';

const SIZE_UNITS = ['S', 'M', 'L', 'XL', 'XXL'];

const toSlug = (name) =>
    name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || '';

const getVariantLabel = (variant) => {
    if (!variant) return 'Default';
    const name = (variant.variantName || '').trim();
    const unit = (variant.unit || '').trim();
    if (SIZE_UNITS.includes(unit.toUpperCase())) return unit.toUpperCase();
    if (name.toUpperCase() === unit.toUpperCase()) return unit;
    if (name && unit) return `${name} ${unit}`;
    return name || unit || 'Default';
};

const getCustomerId = () => {
    const customer = JSON.parse(localStorage.getItem("customer"));
    return customer?.id || null;
};

const clearIfUserChanged = (customerId) => {
    const storedOwnerId = localStorage.getItem('wishlistOwnerId');
    if (storedOwnerId && storedOwnerId !== String(customerId)) {
        localStorage.removeItem('wishlist');
        localStorage.removeItem('cart');
        localStorage.removeItem('cartOwnerId');
    }
    if (customerId) {
        localStorage.setItem('wishlistOwnerId', String(customerId));
    }
};

// ── Toast Component ──
const Toast = ({ toast }) => {
    if (!toast) return null;
    const isError   = toast.type === 'error';
    const isWarning = toast.type === 'warning';
    return (
        <div style={{
            position:     'fixed',
            top:          24,
            right:        24,
            zIndex:       9999,
            background:   isError ? '#FEF2F2' : isWarning ? '#FFFBEB' : '#F0FDF4',
            border:       `1.5px solid ${isError ? '#FECACA' : isWarning ? '#FDE68A' : '#BBF7D0'}`,
            borderRadius: 12,
            padding:      '14px 20px',
            display:      'flex',
            alignItems:   'center',
            gap:          10,
            boxShadow:    '0 4px 24px rgba(0,0,0,0.10)',
            minWidth:     260,
            animation:    'toast-in 0.3s ease',
        }}>
            <span style={{ fontSize: 20 }}>
                {isError ? '❌' : isWarning ? '⚠️' : '✅'}
            </span>
            <div>
                <div style={{ fontWeight: 700, fontSize: 13, color: isError ? '#B91C1C' : isWarning ? '#92400E' : '#15803D' }}>
                    {toast.title}
                </div>
                {toast.msg && (
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{toast.msg}</div>
                )}
            </div>
            <style>{`@keyframes toast-in { from { opacity:0; transform:translateY(-12px); } to { opacity:1; transform:translateY(0); } }`}</style>
        </div>
    );
};

const WishListSection = () => {
    const [items,       setItems]       = useState([]);
    const [cartIds,     setCartIds]     = useState(new Set());
    const [loading,     setLoading]     = useState(true);
    const [removing,    setRemoving]    = useState(null);
    const [addedToCart, setAddedToCart] = useState(null);
    const [error,       setError]       = useState(null);
    const [toast,       setToast]       = useState(null);
    const customerId = getCustomerId();
    const navigate   = useNavigate();

    const showToast = (title, msg = '', type = 'success') => {
        setToast({ title, msg, type });
        setTimeout(() => setToast(null), 2500);
    };

    // ── Re-sync when localStorage changes ──
    useEffect(() => {
        if (!customerId) return;
        const handleStorage = () => { loadData(); };
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const loadData = async () => {
        setLoading(true);
        clearIfUserChanged(customerId);

        try {
            const localWishlist = JSON.parse(localStorage.getItem('wishlist')) || [];

            // Logged-in: fetch from API + merge localStorage (dedupe by productId)
            const [wRes, cRes] = await Promise.all([
                getWishlist(customerId),
                getCart(customerId),
            ]);

            const apiItems  = wRes.data  || [];
            const cartItems = cRes.data  || [];

            const ids = new Set(
                cartItems.map(c => `${c.productId}-${c.variantId || 'default'}`)
            );
            setCartIds(ids);

            // Merge: start with API items, add localStorage items not already present
            const apiProductIds = new Set(apiItems.map(i => String(i.productId)));
            const localOnly = localWishlist
                .filter(i => !apiProductIds.has(String(i.id)))
                .map(i => ({
                    ...i,
                    productId: i.id,
                    name:      i.productName || i.name,
                }));

            const merged = [...apiItems, ...localOnly];

            const itemsWithVariants = await Promise.all(
                merged.map(async (item) => {
                    try {
                        const vRes     = await API.get(`/variants/product/${item.productId}`);
                        const variants = vRes.data?.data || vRes.data || [];
                        return { ...item, variants };
                    } catch {
                        return { ...item, variants: [] };
                    }
                })
            );

            setItems(itemsWithVariants);
        } catch {
            setError("Failed to load wishlist. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!customerId) return;
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const handleProductClick = (item) => {
        navigate(`/product/${toSlug(item.name)}`, {
            state: {
                product: {
                    id:          item.productId,
                    productName: item.name,
                    name:        item.name,
                    price:       item.price,
                    img:         getImageUrl(item.img),
                    image:       item.img,
                    salesPrice:  item.price,
                    mrp:         item.mrp || item.price,
                    weight:      item.weight,
                }
            }
        });
    };

    const handleRemove = async (id) => {
        setRemoving(id);
        try {
            const item = items.find(i => i.id === id);
            if (item) {
                try { await removeFromWishlist(id); } catch { /* ignore API errors for local-only items */ }
            }

            // Always remove from localStorage too
            const localWishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
            const productId = item?.productId;
            const updated = localWishlist.filter(i => String(i.id) !== String(productId));
            localStorage.setItem('wishlist', JSON.stringify(updated));
            window.dispatchEvent(new Event('storage'));

            setTimeout(() => {
                setItems(prev => prev.filter(i => i.id !== id));
                setRemoving(null);
            }, 300);
        } catch {
            setRemoving(null);
            showToast('Error', 'Could not remove item. Please try again.', 'error');
        }
    };

    const handleAddToCart = async (item) => {
        const variants       = item.variants || [];
        const matchedVariant =
            variants.find(v => getVariantLabel(v) === item.weight) ||
            variants[0];

        const stockQty = parseInt(matchedVariant?.stock ?? 999);

        if (stockQty === 0) {
            showToast('Out of Stock', 'This item is currently out of stock.', 'error');
            return;
        }

        const variantId   = matchedVariant?.id || null;
        const variantName = matchedVariant?.variantName || '';
        const unit        = matchedVariant?.unit || '';
        const weight      = getVariantLabel(matchedVariant) || item.weight || '';
        const price       = parseFloat(matchedVariant?.salesPrice || item.price) || 0;
        const cartId      = `${item.productId}-${variantId || 'default'}`;

        if (cartIds.has(cartId)) {
            showToast('Already in Cart!', `${item.name} is already in your cart.`, 'warning');
            return;
        }

        setAddedToCart(item.id);
        try {
            const existingCart = JSON.parse(localStorage.getItem('cart')) || [];
            existingCart.push({
                cartId,
                id:          item.productId,
                variantId:   variantId || 'default',
                name:        item.name,
                variantName,
                unit,
                weight,
                price,
                img:         item.img,
                quantity:    1,
                stock:       stockQty,
            });
            localStorage.setItem('cart', JSON.stringify(existingCart));
            window.dispatchEvent(new Event('storage'));

            await API.post('/cart/add', {
                customerId,
                productId:   item.productId,
                variantId:   variantId || null,
                variantName: variantName || null,
                name:        item.name,
                img:         item.img,
                price,
                quantity:    1,
                weight,
            });
            window.dispatchEvent(new Event('cartUpdated'));

            setCartIds(prev => new Set([...prev, cartId]));
            showToast('Added to Cart!', `${item.name} has been added to your cart.`, 'success');

        } catch {
            showToast('Error', 'Could not add to cart. Please try again.', 'error');
        }
        setTimeout(() => setAddedToCart(null), 1500);
    };

    // ── Guard: Not logged in ──────────────────────────────────────────────────
    if (!customerId) return (
        <section className="km-page">
            <style>{KM_VARS + KM_BASE}</style>
            <div className="km-full">
                <div className="km-empty" style={{ margin: '0 auto' }}>
                    <div className="km-empty-icon">🔒</div>
                    <h4>Please log in to view your wishlist</h4>
                    <p>Sign in to access your saved wishlist items.</p>
                    <button className="km-btn-primary" onClick={() => navigate('/account')}>
                        Go to Login <i className="ph ph-arrow-right" />
                    </button>
                </div>
            </div>
        </section>
    );

    if (loading) return (
        <section className="km-page">
            <style>{KM_VARS + KM_BASE}</style>
            <div className="km-full">
                <div className="km-loader"><div className="km-spinner" /><p>Loading your wishlist…</p></div>
            </div>
        </section>
    );

    return (
        <section className="km-page">
            <Toast toast={toast} />
            <style>{KM_VARS + KM_BASE + WL_STYLES}</style>
            <div className="km-full">
                {error && <div className="km-err">{error}</div>}

                <div className="km-ph">
                    <h1>♥ My Wishlist</h1>
                    <p>Items you love, saved for later</p>
                </div>

                {items.length > 0 ? (
                    <div className="km-card">
                        <div className="km-card-hd">
                            <span style={{ marginRight: 'auto', fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
                                ♥ Wishlist
                            </span>
                        </div>

                        <div className="km-table-wrap">
                            <table className="km-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 48 }}></th>
                                        <th>Product Details</th>
                                        <th>Price</th>
                                        <th>Variant</th>
                                        <th>Availability</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map(item => {
                                        const variants = item.variants || [];
                                        const matchedVariant =
                                            variants.find(v => getVariantLabel(v) === item.weight) ||
                                            variants[0];

                                        const stockQty     = parseInt(matchedVariant?.stock ?? (item.stock ? 999 : 0));
                                        const isOutOfStock = stockQty === 0;
                                        const isLowStock   = stockQty > 0 && stockQty <= 5;

                                        const variantId = matchedVariant?.id || null;
                                        const cartId    = `${item.productId}-${variantId || 'default'}`;
                                        const isInCart  = cartIds.has(cartId);

                                        return (
                                            <tr
                                                key={item.id}
                                                style={{
                                                    opacity:    removing === item.id ? 0 : isOutOfStock ? 0.6 : 1,
                                                    transform:  removing === item.id ? 'translateX(-16px)' : 'none',
                                                    transition: 'all 0.3s',
                                                }}
                                            >
                                                {/* ── Remove ── */}
                                                <td>
                                                    <button className="km-rm-btn" onClick={() => handleRemove(item.id)} title="Remove from wishlist">
                                                        <i className="ph ph-x" />
                                                    </button>
                                                </td>

                                                {/* ── Product ── */}
                                                <td>
                                                    <div className="km-prod-cell">
                                                        <div className="km-img-w">
                                                            <img src={getImageUrl(item.img)} alt={item.name} />
                                                        </div>
                                                        <div>
                                                            <span
                                                                className="km-pname"
                                                                onClick={() => handleProductClick(item)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                {item.name}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* ── Price ── */}
                                                <td>
                                                    <span className="km-price">₹{item.price}</span>
                                                </td>

                                                {/* ── Variant pill ── */}
                                                <td>
                                                    {item.weight ? (
                                                        <span style={{
                                                            display:      'inline-block',
                                                            padding:      '3px 10px',
                                                            background:   '#FFF4EC',
                                                            border:       '1.5px solid #F57C00',
                                                            borderRadius: 6,
                                                            fontSize:     12,
                                                            fontWeight:   700,
                                                            color:        '#C2410C',
                                                        }}>
                                                            {[...new Set(item.weight.trim().split(/\s+/))].join(' ')}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>
                                                    )}
                                                </td>

                                                {/* ── Availability ── */}
                                                <td>
                                                    {isOutOfStock ? (
                                                        <span className="km-out">
                                                            <span className="km-dout" />
                                                            Out of Stock
                                                        </span>
                                                    ) : isLowStock ? (
                                                        <span style={{ color: '#C2410C', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                                                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#F59E0B', display: 'inline-block' }} />
                                                            Only {stockQty} left!
                                                        </span>
                                                    ) : (
                                                        <span className="km-in">
                                                            <span className="km-din" />
                                                            In Stock
                                                        </span>
                                                    )}
                                                </td>

                                                {/* ── Action button ── */}
                                                <td>
                                                    {isInCart ? (
                                                        <button
                                                            className="km-btn-outline"
                                                            onClick={() => navigate('/cart')}
                                                            style={{
                                                                display:      'inline-flex',
                                                                alignItems:   'center',
                                                                gap:          6,
                                                                fontSize:     13,
                                                                padding:      '7px 14px',
                                                                borderRadius: 8,
                                                                cursor:       'pointer',
                                                                whiteSpace:   'nowrap',
                                                            }}
                                                        >
                                                            <i className="ph ph-shopping-cart" style={{ fontSize: 14 }} /> Go to Cart
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className={`km-btn-orange ${addedToCart === item.id ? 'added' : ''}`}
                                                            disabled={isOutOfStock}
                                                            onClick={() => handleAddToCart(item)}
                                                            style={{
                                                                opacity: isOutOfStock ? 0.5 : 1,
                                                                cursor:  isOutOfStock ? 'not-allowed' : 'pointer',
                                                            }}
                                                        >
                                                            {addedToCart === item.id ? (
                                                                <><i className="ph ph-check" style={{ fontSize: 14 }} /> Added!</>
                                                            ) : isOutOfStock ? (
                                                                <><i className="ph ph-x-circle" style={{ fontSize: 14 }} /> Out of Stock</>
                                                            ) : (
                                                                <><i className="ph ph-shopping-cart" style={{ fontSize: 14 }} /> Add to Cart</>
                                                            )}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="km-card-ft">
                            <button className="km-btn-outline" onClick={() => navigate('/shop')}>
                                <i className="ph ph-arrow-left" /> Continue Shopping
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="km-empty">
                        <div className="km-empty-icon">♥</div>
                        <h4>Your wishlist is empty</h4>
                        <p>Add items you love to keep track of them!</p>
                        <button className="km-btn-primary" onClick={() => navigate('/shop')}>
                            Go to Shop <i className="ph ph-arrow-right" />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

const WL_STYLES = `
  /* Mobile Responsive Table */
  @media (max-width: 768px) {
    .km-table-wrap {
      border: none;
    }
    
    .km-table thead {
      display: none;
    }
    
    .km-table, .km-table tbody, .km-table tr, .km-table td {
      display: block;
      width: 100%;
    }
    
    .km-table tr {
      background: #fff;
      border: 1px solid #eee;
      border-radius: 12px;
      margin-bottom: 16px;
      padding: 12px;
      position: relative;
    }
    
    .km-table td {
      text-align: right;
      padding: 8px 0;
      border: none;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
    }
    
    .km-table td:nth-child(2) {
      justify-content: flex-start;
      border-bottom: 1px solid #f5f5f5;
      padding-bottom: 12px;
      margin-bottom: 8px;
    }

    .km-table td:nth-child(3):before { content: "Price"; font-weight: 600; color: #666; }
    .km-table td:nth-child(4):before { content: "Variant"; font-weight: 600; color: #666; }
    .km-table td:nth-child(5):before { content: "Status"; font-weight: 600; color: #666; }
    
    .km-table td:nth-child(1) {
      position: absolute;
      top: 10px;
      right: 10px;
      width: auto;
      z-index: 10;
    }
    
    .km-table td:nth-child(6) {
      margin-top: 10px;
      display: block;
    }
    
    .km-table td:nth-child(6) button {
      width: 100%;
      justify-content: center;
      padding: 12px;
    }

    .km-prod-cell {
      gap: 12px;
    }
    
    .km-img-w {
      width: 60px;
      height: 60px;
    }
    
    .km-pname {
      font-size: 15px;
      max-width: 180px;
      text-align: left;
    }
  }

  @keyframes toast-in { 
    from { opacity: 0; transform: translateY(-20px) scale(0.95); } 
    to { opacity: 1; transform: translateY(0) scale(1); } 
  }
`;

export default WishListSection;